/* ============================================================
   EI Cart Popover V1 — Logique JS
   Vanilla JS (compatible Safari 14+, Chrome 90+, Firefox 90+)
   Co-existe avec Vegai 1.0.0 jQuery sans modifier ses classes.
   ============================================================ */

(function () {
  'use strict';

  /* ===== Configuration ===== */
  const CONFIG = {
    SEUIL_RELAIS_CENTS: 4900,
    SEUIL_DOMICILE_CENTS: 9000,
    SECTION_ID: 'ei-cart-popover',
    STORAGE_KEY_DISMISSED: 'ei_threshold_dismissed',
    BROADCAST_CHANNEL_NAME: 'ei-cart-sync',
    SLOGANS: [
      "« 4,9/5 — Plus de 1 600 avis clients parlent pour nous. »",
      "« Depuis 2014. 20 régions d'Italie. 70 producteurs artisans. »",
      "« On choisit nos producteurs comme on choisit ses amis : un par un. »",
      "« Margot & Lucas, frère et sœur. L'Italie, en direct des artisans. »"
    ],
    JOURS_FERIES_2026: [
      '2026-01-01', '2026-04-06', '2026-05-01', '2026-05-08',
      '2026-05-14', '2026-05-25', '2026-07-14', '2026-08-15',
      '2026-11-01', '2026-11-11', '2026-12-25'
    ],
    JOURS_SEMAINE_FR: ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi']
  };

  /* ===== State ===== */
  let cart = null;
  let isOpen = false;
  let currentMode = 'extended';
  let lastFocusedElement = null;
  let lastSubBeforePromoCents = 0;
  let broadcastChannel = null;
  let isSyncingFromOtherTab = false;

  /* ===== Utils ===== */
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  function moneyFormat(cents) {
    const eur = cents / 100;
    return eur.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
  }

  /* Shopify Storefront API renvoie les prix tantôt en décimales ('9.30')
     tantôt en cents entiers ('930'). On détecte le format via la présence
     d'un point/virgule décimal. */
  function parsePriceToCents(rawPrice) {
    if (rawPrice === null || rawPrice === undefined) return 0;
    const s = String(rawPrice);
    if (s.indexOf('.') !== -1 || s.indexOf(',') !== -1) {
      return Math.round(parseFloat(s.replace(',', '.')) * 100);
    }
    return parseInt(s, 10) || 0;
  }

  function getScrollbarWidth() {
    return window.innerWidth - document.documentElement.clientWidth;
  }

  function lockBodyScroll() {
    const sbw = getScrollbarWidth();
    document.body.style.setProperty('--ei-scrollbar-width', sbw + 'px');
    document.body.classList.add('ei-cp-body-locked');
  }

  function unlockBodyScroll() {
    document.body.classList.remove('ei-cp-body-locked');
    document.body.style.removeProperty('--ei-scrollbar-width');
  }

  function pushGA4(eventName, extra = {}) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({
      event: eventName,
      ei_version: 'v1',
      cart_total: cart ? cart.total_price / 100 : 0,
      cart_items_count: cart ? cart.item_count : 0
    }, extra));
  }

  function dispatchEi(eventName, detail = {}) {
    document.dispatchEvent(new CustomEvent(eventName, { detail }));
  }

  /* ===== Cart AJAX API ===== */
  async function fetchCart() {
    try {
      const res = await fetch('/cart.js', { credentials: 'same-origin' });
      cart = await res.json();
      return cart;
    } catch (err) {
      console.error('[ei-cart] fetchCart failed', err);
      return null;
    }
  }

  async function addToCart(variantId, quantity = 1, properties = {}) {
    const body = { id: variantId, quantity: quantity };
    if (Object.keys(properties).length) body.properties = properties;
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok) throw data;
      await fetchCart();
      return data;
    } catch (err) {
      console.error('[ei-cart] addToCart failed', err);
      throw err;
    }
  }

  async function changeLineByKey(lineKey, quantity) {
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: lineKey, quantity: quantity })
      });
      cart = await res.json();
      return cart;
    } catch (err) {
      console.error('[ei-cart] changeLine failed', err);
      throw err;
    }
  }

  async function applyDiscount(code) {
    /* Shopify n'a pas d'endpoint AJAX direct pour les codes promo.
       L'astuce standard : fetch GET /discount/CODE pose un cookie discount,
       ensuite refresh cart via /cart.js le voit dans cart_level_discount_applications. */
    try {
      await fetch('/discount/' + encodeURIComponent(code), {
        method: 'GET',
        credentials: 'same-origin',
        redirect: 'manual'
      }).catch(() => {});
      /* Force la récup du cart pour appliquer le discount */
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ attributes: { ei_promo_attempt: code } })
      });
      await fetchCart();
      return cart && cart.cart_level_discount_applications.length > 0;
    } catch (err) {
      console.error('[ei-cart] applyDiscount failed', err);
      return false;
    }
  }

  async function removeDiscount() {
    try {
      await fetch('/discount/clear', { method: 'GET', credentials: 'same-origin' }).catch(() => {});
      await fetchCart();
    } catch (err) {
      console.error('[ei-cart] removeDiscount failed', err);
    }
  }

  /* ===== Section refresh via Shopify Sections API ===== */
  async function refreshPopoverFromSection() {
    try {
      const url = window.location.pathname + '?sections=' + CONFIG.SECTION_ID;
      const res = await fetch(url, { credentials: 'same-origin' });
      const json = await res.json();
      const html = json[CONFIG.SECTION_ID];
      if (!html) return false;
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newOverlay = doc.querySelector('[data-ei-cart-popover]');
      const currentOverlay = $('[data-ei-cart-popover]');
      if (!newOverlay || !currentOverlay) return false;
      /* Préserver l'état hidden/open du container actuel */
      const wasHidden = currentOverlay.hasAttribute('hidden');
      currentOverlay.innerHTML = newOverlay.innerHTML;
      currentOverlay.dataset.eiEmpty = newOverlay.dataset.eiEmpty;
      currentOverlay.dataset.eiCampagneLive = newOverlay.dataset.eiCampagneLive;
      if (wasHidden) currentOverlay.setAttribute('hidden', '');
      else currentOverlay.removeAttribute('hidden');
      updateCartBadge(cart ? cart.item_count : 0);
      updateShippingMessage();
      randomizeTrustBar();
      updateCheckoutAmount();
      /* Fallback recos : si Liquid n'a rendu aucune reco (metafield vide), fetch via API native Shopify */
      loadRecosFallback();
      return true;
    } catch (err) {
      console.error('[ei-cart] refreshPopoverFromSection failed', err);
      return false;
    }
  }

  /* ===== Open / close popover ===== */
  function openPopover(mode = 'extended') {
    const overlay = $('[data-ei-cart-popover]');
    if (!overlay) return;
    if (isOpen) return;

    lastFocusedElement = document.activeElement;
    currentMode = mode;
    const container = $('[data-ei-cart-container]', overlay);
    if (container) {
      container.classList.remove('ei-cp-mode-compact', 'ei-cp-mode-extended');
      container.classList.add('ei-cp-mode-' + mode);
    }
    overlay.dataset.eiMode = mode;

    overlay.removeAttribute('hidden');
    overlay.classList.remove('is-closing');
    isOpen = true;
    lockBodyScroll();
    randomizeTrustBar();
    updateShippingMessage();
    setTimeout(() => {
      if (container) container.focus({ preventScroll: true });
    }, 100);

    pushGA4('ei_drawer_open', {
      ei_source: mode === 'compact' ? 'popover_atc' : 'cart_icon',
      ei_action: 'open'
    });

    /* Update last_threshold_suggestion_shown_at metafield si applicable au open ? Non : géré dans Smart Threshold */
  }

  function closePopover() {
    const overlay = $('[data-ei-cart-popover]');
    if (!overlay || !isOpen) return;

    overlay.classList.add('is-closing');
    setTimeout(() => {
      overlay.setAttribute('hidden', '');
      overlay.classList.remove('is-closing');
      unlockBodyScroll();
      isOpen = false;
      if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
        lastFocusedElement.focus();
      }
    }, 300);
  }

  /* ===== Mode compact (après ajout au panier) ===== */
  function buildCompactHtml(addedProduct, recoProduct) {
    const sub = cart ? cart.original_total_price : 0;
    const isCampagneLive = ($('[data-ei-cart-popover]') || {}).dataset && $('[data-ei-cart-popover]').dataset.eiCampagneLive === 'true';
    const seuilCampagne = parseInt(($('[data-ei-cart-popover]').dataset.eiCampagneSeuil || '30'), 10) * 100;
    let progressHtml = '';

    if (isCampagneLive) {
      const reached = sub >= seuilCampagne;
      const pct = reached ? 100 : (sub * 100.0 / seuilCampagne);
      const delta = (seuilCampagne - sub) / 100;
      progressHtml = `
        <div class="ei-cp-progress" data-ei-cart-progress>
          <div class="ei-cp-progress-msg" data-ei-progress-msg>${reached ? '🎉 Livraison offerte !' : '📦 Plus que <strong>' + delta.toFixed(2).replace('.', ',') + ' €</strong> pour la livraison offerte'}</div>
          <div class="ei-cp-progress-track" role="progressbar" aria-valuemin="0" aria-valuemax="${seuilCampagne / 100}" aria-valuenow="${(sub / 100).toFixed(2)}">
            <div class="ei-cp-progress-fill ${reached ? 'is-success' : ''}" style="width: ${pct}%;" data-ei-progress-fill></div>
          </div>
          <div class="ei-cp-progress-marks is-single">
            <span class="ei-cp-progress-mark ${reached ? 'is-reached' : ''}">${seuilCampagne / 100} €${reached ? ' ✓' : ''}</span>
          </div>
        </div>`;
    } else {
      const relaisReached = sub >= CONFIG.SEUIL_RELAIS_CENTS;
      const domicileReached = sub >= CONFIG.SEUIL_DOMICILE_CENTS;
      const pct = (sub * 100.0 / CONFIG.SEUIL_DOMICILE_CENTS);
      const seuil1Pct = CONFIG.SEUIL_RELAIS_CENTS * 100.0 / CONFIG.SEUIL_DOMICILE_CENTS;
      let msg, fillClass;
      if (domicileReached) {
        msg = '🎉 Livraison à domicile offerte !';
        fillClass = 'is-success';
      } else if (relaisReached) {
        const delta = (CONFIG.SEUIL_DOMICILE_CENTS - sub) / 100;
        msg = '🎉 Relais offerte. Plus que <strong>' + delta.toFixed(2).replace('.', ',') + ' €</strong> pour la domicile';
        fillClass = 'is-mixed';
      } else {
        const delta = (CONFIG.SEUIL_RELAIS_CENTS - sub) / 100;
        msg = '📦 Plus que <strong>' + delta.toFixed(2).replace('.', ',') + ' €</strong> pour la livraison gratuite en relais';
        fillClass = '';
      }
      progressHtml = `
        <div class="ei-cp-progress" data-ei-cart-progress>
          <div class="ei-cp-progress-msg" data-ei-progress-msg>${msg}</div>
          <div class="ei-cp-progress-track" role="progressbar" aria-valuemin="0" aria-valuemax="90" aria-valuenow="${(sub / 100).toFixed(2)}">
            <div class="ei-cp-progress-fill ${fillClass}" style="width: ${Math.min(100, pct)}%; --ei-seuil1: ${seuil1Pct}%;" data-ei-progress-fill></div>
          </div>
          <div class="ei-cp-progress-marks">
            <span class="ei-cp-progress-mark ${relaisReached ? 'is-reached' : ''}" style="position: relative; left: ${seuil1Pct}%; transform: translateX(-50%);">49 €${relaisReached ? ' ✓' : ''}</span>
            <span class="ei-cp-progress-mark ${domicileReached ? 'is-reached' : ''}">90 €${domicileReached ? ' ✓' : ''}</span>
          </div>
        </div>`;
    }

    /* Sprint V2 2026-05-07 : détection mode smart-threshold (proche d'un seuil de livraison) */
    const isSmartMode = (
      (sub >= CONFIG.SEUIL_RELAIS_CENTS - 500 && sub < CONFIG.SEUIL_RELAIS_CENTS) ||
      (sub >= CONFIG.SEUIL_DOMICILE_CENTS - 500 && sub < CONFIG.SEUIL_DOMICILE_CENTS)
    );

    let recoHtml = '';
    if (recoProduct) {
      const stickerLabel = isSmartMode ? '🎯 Petit complément' : 'Suggéré ✨';
      const recoTitle = isSmartMode
        ? `<em>Pour décrocher la <strong>livraison offerte</strong>… ces petits plaisirs feront pile l'affaire.</em>`
        : `<em>Pour aller plus loin… <strong>Margot</strong> y a pensé pour vous.</em>`;
      recoHtml = `
        <div class="ei-cp-recos ei-cp-recos--v2${isSmartMode ? ' is-smart-mode' : ''}" data-ei-smart-mode="${isSmartMode}">
          <span class="ei-cp-recos-sticker" aria-hidden="true">${stickerLabel}</span>
          <h3 class="ei-cp-recos-title-v2">${recoTitle}</h3>
          <div class="ei-cp-reco" data-ei-cart-reco data-ei-product-handle="${recoProduct.handle}">
            <a href="${recoProduct.url}" class="ei-cp-reco-img" tabindex="-1" aria-hidden="true">
              ${recoProduct.featured_image ? `<img src="${recoProduct.featured_image.replace(/(\.\w+)(\?|$)/, '_108x$1$2')}" alt="" width="54" height="54" loading="lazy">` : '<span aria-hidden="true">🛒</span>'}
            </a>
            <div class="ei-cp-reco-info">
              <p class="ei-cp-reco-name"><a href="${recoProduct.url}">${escapeHtml(recoProduct.title)}</a></p>
              <div class="ei-cp-reco-price">${moneyFormat(recoProduct.price)}</div>
            </div>
            <button type="button" class="ei-cp-reco-add" data-ei-cart-action="add-reco" data-ei-product-handle="${recoProduct.handle}" data-ei-variant-id="${recoProduct.variant_id || ''}" aria-label="Ajouter ${escapeHtml(recoProduct.title)} au panier">+</button>
          </div>
        </div>`;
    }

    const sloganIdx = Math.floor(Math.random() * CONFIG.SLOGANS.length);
    const trustHtml = `
      <div class="ei-cp-trust" data-ei-cart-trust>
        <span class="ei-cp-trust-star" aria-hidden="true">★</span>
        <em class="ei-cp-trust-text" data-ei-trust-text>${CONFIG.SLOGANS[sloganIdx]}</em>
      </div>`;

    return `
      <header class="ei-cp-header ei-cp-header--compact-v2">
        <div class="ei-cp-header-row">
          <span class="ei-cp-header-icon-wrap" aria-hidden="true">
            <img src="/cdn/shop/files/filet-panier-EI-PNG.png?v=1778157801" alt="" width="40" height="40" loading="eager">
          </span>
          <span class="ei-cp-header-signature" aria-hidden="true">Bien choisi !</span>
        </div>
        <button type="button" class="ei-cp-close" data-ei-cart-action="close" aria-label="Fermer le panier"><span aria-hidden="true">✕</span></button>
      </header>
      <div class="ei-cp-body" data-ei-cart-body>
        <div class="ei-cp-section">
          <div class="ei-cp-added-card">
            <div class="ei-cp-added-img">
              ${addedProduct.image ? `<img src="${addedProduct.image}" alt="" width="78" height="78" loading="eager">` : '<span aria-hidden="true">🛒</span>'}
            </div>
            <div class="ei-cp-added-info">
              <div class="ei-cp-added-name">${escapeHtml(addedProduct.title)}</div>
              <div class="ei-cp-added-price">${moneyFormat(addedProduct.line_price)}</div>
            </div>
          </div>
        </div>
        <div class="ei-cp-section">
          <h3 class="ei-cp-section-title">Livraison</h3>
          ${progressHtml}
        </div>
        ${recoHtml}
        ${trustHtml}
      </div>
      <div class="ei-cp-compact-ctas">
        <button type="button" class="ei-cp-btn-secondary" data-ei-cart-action="continue">Continuer mes achats</button>
        <button type="button" class="ei-cp-btn-primary" data-ei-cart-action="view-cart">Voir mon panier (${cart ? cart.item_count : 1})</button>
      </div>
    `;
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  async function showCompactConfirmation(addedProduct) {
    const overlay = $('[data-ei-cart-popover]');
    const container = $('[data-ei-cart-container]', overlay);
    if (!overlay || !container) return;

    /* Tente de récupérer 1 reco compagnon */
    let recoProduct = null;
    try {
      const recosUrl = '/recommendations/products?product_id=' + addedProduct.product_id + '&limit=1&intent=related';
      const recoRes = await fetch(recosUrl + '&section_id=', { credentials: 'same-origin' }).catch(() => null);
      if (recoRes && recoRes.ok) {
        /* Endpoint sans section_id renvoie HTML, avec section_id renvoie JSON.
           Plus fiable : utiliser /recommendations/products.json */
      }
      const jsonRes = await fetch('/recommendations/products.json?product_id=' + addedProduct.product_id + '&limit=1&intent=related', { credentials: 'same-origin' });
      if (jsonRes.ok) {
        const data = await jsonRes.json();
        if (data.products && data.products.length) {
          const p = data.products[0];
          const firstVariant = (p.variants || []).find(v => v.available) || (p.variants || [])[0];
          recoProduct = {
            handle: p.handle,
            url: '/products/' + p.handle,
            title: p.title,
            price: parsePriceToCents(firstVariant ? firstVariant.price : p.price),
            featured_image: p.featured_image,
            variant_id: firstVariant ? firstVariant.id : null
          };
        }
      }
    } catch (e) { /* silencieux : reco optionnelle */ }

    container.innerHTML = buildCompactHtml(addedProduct, recoProduct);
    container.classList.remove('ei-cp-mode-extended');
    container.classList.add('ei-cp-mode-compact');
    overlay.dataset.eiMode = 'compact';
    currentMode = 'compact';
    if (!isOpen) {
      openPopover('extended');
    }
  }

  /* ===== Add to cart interception ===== */
  function interceptAddToCart() {
    /* Click capture-phase sur les boutons ATC pour neutraliser les handlers jQuery Vegai
       qui ouvriraient le drawer historique (ajax-cart.js). Le submit handler ci-dessous
       prend ensuite le relais sur le form. */
    document.addEventListener('click', (e) => {
      const atcBtn = e.target.closest(
        '.enj-add-to-cart-btn, ' +
        '.ei-grid__add-btn, ' +
        'button[name="add"], ' +
        'input[name="add"]'
      );
      if (!atcBtn) return;
      const form = atcBtn.closest('form[action*="/cart/add"]');
      if (!form) return;
      /* Stoppe la propagation pour empêcher les handlers click du thème (jQuery)
         de déclencher le drawer / popup historique. Le submit événement
         qui suit sera capturé par notre listener submit ci-dessous. */
      e.stopPropagation();
    }, true);

    document.addEventListener('submit', async (e) => {
      const form = e.target;
      if (!form || !form.matches('form[action*="/cart/add"]')) return;
      e.preventDefault();
      e.stopPropagation();

      const submitBtn = form.querySelector('[type="submit"], button[name="add"]');
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.dataset.eiOrigText = submitBtn.innerText || submitBtn.value;
      }

      try {
        const formData = new FormData(form);
        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity') || '1', 10);

        /* Fix Bug #4 (2026-05-08) : si le form a un data-bp-discount-code,
           on active le code promo via /discount/CODE avant l'ajout. Le cookie
           ainsi posé sera vu par /cart/add.js et appliqué automatiquement. */
        const bpDiscountCode = form.dataset.bpDiscountCode;
        if (bpDiscountCode) {
          await fetch('/discount/' + encodeURIComponent(bpDiscountCode), {
            method: 'GET',
            credentials: 'same-origin',
            redirect: 'manual'
          }).catch(() => {});
        }

        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Accept': 'application/json' },
          credentials: 'same-origin',
          body: formData
        });
        const data = await res.json();
        if (!res.ok) {
          showAddError(form, data);
          return;
        }

        await fetchCart();
        updateCartBadge(cart.item_count);

        const added = {
          variant_id: data.variant_id || data.id,
          product_id: data.product_id,
          title: data.product_title || data.title,
          image: data.image || data.featured_image || (data.product && data.product.featured_image),
          line_price: data.final_line_price || data.line_price || (data.price * quantity),
          quantity: data.quantity || quantity
        };

        /* Sprint 2026-05-07 (Margot decision) : popup à l'ajout = LE PANIER complet (extended).
           Plus de showCompactConfirmation. */
        await refreshPopoverFromSection();
        openPopover('extended');
        notifyOtherTabs('cart-updated');
        pushGA4('ei_cart_add', {
          ei_source: 'product_page',
          ei_action: 'add',
          ei_product: data.handle || data.product_handle
        });

        /* Détection franchissement seuil */
        checkThresholdCrossings(lastSubBeforePromoCents, cart.original_total_price);
        lastSubBeforePromoCents = cart.original_total_price;

      } catch (err) {
        console.error('[ei-cart] add to cart failed', err);
        showAddError(form, err);
      } finally {
        if (submitBtn) {
          submitBtn.disabled = false;
          if (submitBtn.dataset.eiOrigText) {
            if (submitBtn.tagName === 'BUTTON') submitBtn.innerText = submitBtn.dataset.eiOrigText;
            else submitBtn.value = submitBtn.dataset.eiOrigText;
            delete submitBtn.dataset.eiOrigText;
          }
        }
      }
    }, true); /* capture phase pour intercepter avant les listeners du thème */
  }

  function showAddError(form, errData) {
    const msg = (errData && errData.description) || 'Stock épuisé pour ce produit';
    const existing = form.querySelector('.ei-cp-add-error');
    if (existing) existing.remove();
    const div = document.createElement('div');
    div.className = 'ei-cp-add-error';
    div.style.cssText = 'color:#FF6B4A;font-size:12px;margin-top:8px;';
    div.textContent = msg;
    form.appendChild(div);
    setTimeout(() => div.remove(), 5000);
  }

  /* ===== Threshold crossings (animations + GA4) ===== */
  function checkThresholdCrossings(beforeCents, afterCents) {
    const overlay = $('[data-ei-cart-popover]');
    if (!overlay) return;
    const isCampagneLive = overlay.dataset.eiCampagneLive === 'true';
    const seuilCampagne = parseInt(overlay.dataset.eiCampagneSeuil || '30', 10) * 100;

    const fill = $('[data-ei-progress-fill]');

    if (isCampagneLive) {
      if (beforeCents < seuilCampagne && afterCents >= seuilCampagne) {
        if (fill) { fill.classList.add('is-flash'); setTimeout(() => fill.classList.remove('is-flash'), 700); }
        pushGA4('ei_threshold_reached_30', { ei_source: 'cart', ei_action: 'threshold_reached' });
      } else if (beforeCents >= seuilCampagne && afterCents < seuilCampagne) {
        if (fill) { fill.classList.add('is-pulse'); setTimeout(() => fill.classList.remove('is-pulse'), 500); }
      }
    } else {
      if (beforeCents < CONFIG.SEUIL_RELAIS_CENTS && afterCents >= CONFIG.SEUIL_RELAIS_CENTS) {
        if (fill) { fill.classList.add('is-flash'); setTimeout(() => fill.classList.remove('is-flash'), 700); }
        pushGA4('ei_threshold_reached_49', { ei_source: 'cart', ei_action: 'threshold_reached' });
      }
      if (beforeCents < CONFIG.SEUIL_DOMICILE_CENTS && afterCents >= CONFIG.SEUIL_DOMICILE_CENTS) {
        if (fill) { fill.classList.add('is-flash-strong'); setTimeout(() => fill.classList.remove('is-flash-strong'), 900); }
        pushGA4('ei_threshold_reached_90', { ei_source: 'cart', ei_action: 'threshold_reached' });
      }
      if (beforeCents >= CONFIG.SEUIL_RELAIS_CENTS && afterCents < CONFIG.SEUIL_RELAIS_CENTS) {
        if (fill) { fill.classList.add('is-pulse'); setTimeout(() => fill.classList.remove('is-pulse'), 500); }
      }
    }
  }

  /* ===== Stepper handlers ===== */
  async function handleStepperClick(btn) {
    const lineKey = btn.dataset.eiLineKey;
    const action = btn.dataset.eiStepperAction;
    if (!lineKey) return;
    const stepper = btn.closest('[data-ei-cart-stepper]');
    const qtySpan = stepper && stepper.querySelector('[data-ei-stepper-qty]');
    if (!qtySpan) return;
    const currentQty = parseInt(qtySpan.textContent, 10) || 0;
    const max = parseInt(btn.dataset.eiStepperMax || '999', 10);
    let newQty = currentQty;
    if (action === 'increment') newQty = Math.min(currentQty + 1, max);
    else if (action === 'decrement') newQty = Math.max(currentQty - 1, 0);
    if (newQty === currentQty) return;
    if (newQty === currentQty && action === 'increment') {
      /* Quantité max atteinte : feedback visuel */
      btn.disabled = true;
      btn.setAttribute('aria-disabled', 'true');
      return;
    }

    /* Optimistic UI */
    qtySpan.textContent = newQty;
    const before = cart ? cart.original_total_price : 0;
    try {
      await changeLineByKey(lineKey, newQty);
      await refreshPopoverFromSection();
      checkThresholdCrossings(before, cart.original_total_price);
      lastSubBeforePromoCents = cart.original_total_price;
      notifyOtherTabs('cart-updated');
      pushGA4('ei_cart_qty_change', {
        ei_source: 'popover_stepper',
        ei_action: 'qty_change'
      });
    } catch (err) {
      qtySpan.textContent = currentQty;
    }
  }

  /* ===== Remove line ===== */
  function handleRemoveClick(btn) {
    const lineKey = btn.dataset.eiLineKey;
    const item = btn.closest('[data-ei-cart-line]');
    if (!item) return;
    const slot = item.querySelector('[data-ei-confirm-slot]');
    if (!slot) return;

    slot.innerHTML = `
      <div class="ei-cp-item-confirm">
        <span>Supprimer ce produit ?</span>
        <div class="ei-cp-item-confirm-actions">
          <button type="button" class="ei-cp-item-confirm-btn is-no" data-ei-confirm="cancel">Non</button>
          <button type="button" class="ei-cp-item-confirm-btn is-yes" data-ei-confirm="confirm" data-ei-line-key="${lineKey}">Oui</button>
        </div>
      </div>`;
    slot.removeAttribute('hidden');
  }

  async function handleRemoveConfirm(btn) {
    const lineKey = btn.dataset.eiLineKey;
    const item = btn.closest('[data-ei-cart-line]');
    if (!item || !lineKey) return;
    item.classList.add('is-removing');
    const before = cart ? cart.original_total_price : 0;
    setTimeout(async () => {
      try {
        await changeLineByKey(lineKey, 0);
        await refreshPopoverFromSection();
        checkThresholdCrossings(before, cart.original_total_price);
        lastSubBeforePromoCents = cart.original_total_price;
        notifyOtherTabs('cart-updated');
        pushGA4('ei_cart_remove', {
          ei_source: 'popover',
          ei_action: 'remove'
        });
        /* Dernier article supprimé → fermer le popover en silence pour éviter
           l'état "page panier vide" plein écran sur mobile (UX frustrante). */
        if (cart && cart.item_count === 0) {
          closePopover();
        }
      } catch (err) {
        item.classList.remove('is-removing');
      }
    }, 300);
  }

  function handleRemoveCancel(btn) {
    const item = btn.closest('[data-ei-cart-line]');
    if (!item) return;
    const slot = item.querySelector('[data-ei-confirm-slot]');
    if (slot) {
      slot.innerHTML = '';
      slot.setAttribute('hidden', '');
    }
  }

  /* ===== Promo handlers ===== */
  function handlePromoToggle() {
    const form = $('[data-ei-cart-promo-form]');
    if (form) {
      form.removeAttribute('hidden');
      const input = form.querySelector('[data-ei-cart-promo-input]');
      if (input) input.focus();
    }
    const toggle = $('[data-ei-cart-promo-toggle]');
    if (toggle) toggle.style.display = 'none';
  }

  async function handlePromoSubmit(form) {
    const input = form.querySelector('[data-ei-cart-promo-input]');
    const submitBtn = form.querySelector('[data-ei-cart-promo-submit]');
    const feedback = $('[data-ei-cart-promo-feedback]');
    if (!input) return;
    const code = input.value.trim().toUpperCase();
    if (!code) return;

    if (submitBtn) submitBtn.disabled = true;
    input.classList.remove('is-success', 'is-error', 'is-warning');
    if (feedback) { feedback.textContent = ''; feedback.setAttribute('hidden', ''); }

    const success = await applyDiscount(code);
    if (success) {
      input.classList.add('is-success');
      pushGA4('ei_promo_attempt', { ei_action: 'promo_success', ei_promo_code: code });
      await refreshPopoverFromSection();
    } else {
      input.classList.add('is-error');
      if (feedback) {
        feedback.textContent = "Code invalide ou non applicable à votre panier";
        feedback.classList.add('is-error');
        feedback.removeAttribute('hidden');
      }
      pushGA4('ei_promo_attempt', { ei_action: 'promo_failed', ei_promo_code: code });
    }

    if (submitBtn) submitBtn.disabled = false;
  }

  async function handlePromoRemove() {
    await removeDiscount();
    await refreshPopoverFromSection();
    notifyOtherTabs('cart-updated');
  }

  /* ===== Reco add ===== */
  async function handleRecoAdd(btn) {
    const variantId = btn.dataset.eiVariantId;
    if (!variantId) return;
    btn.disabled = true;
    const before = cart ? cart.original_total_price : 0;
    try {
      await addToCart(variantId, 1);
      await refreshPopoverFromSection();
      checkThresholdCrossings(before, cart.original_total_price);
      lastSubBeforePromoCents = cart.original_total_price;
      notifyOtherTabs('cart-updated');
      pushGA4('ei_cart_add', {
        ei_source: 'popover_reco',
        ei_action: 'add',
        ei_product: btn.dataset.eiProductHandle
      });
    } catch (err) {
      btn.disabled = false;
    }
  }

  /* ===== Checkout click (intercept pour Smart Threshold) ===== */
  function handleCheckoutClick(e) {
    const btn = e.target.closest('[data-ei-checkout-button]');
    if (!btn) return;

    /* Smart Threshold module écoute cet event et peut preventDefault */
    const customEvent = new CustomEvent('ei:checkout-clicked', {
      detail: {
        cart: cart,
        subBeforePromoCents: cart ? cart.original_total_price : 0
      },
      cancelable: true
    });
    const proceed = document.dispatchEvent(customEvent);
    if (!proceed) {
      e.preventDefault();
      return;
    }

    pushGA4('ei_checkout_initiated', {
      ei_source: 'popover',
      ei_action: 'checkout'
    });
  }

  /* ===== Cart icon badge ===== */
  function updateCartBadge(count) {
    /* Badge natif Vegai .enj-cartcount — préservé */
    /* Bug 2026-05-15 : ajout .js-number-cart + .number-cart pour update le compteur natif Vegai du header desktop (filet panier) qui n'a pas .enj-cartcount */
    $$('.enj-cartcount, [data-cart-count], .js-number-cart, .number-cart').forEach(el => {
      el.textContent = count;
      if (count > 0) el.classList.add('has-items');
      else el.classList.remove('has-items');
    });
  }

  function updateCheckoutAmount() {
    if (!cart) return;
    $$('[data-ei-cta-amount]').forEach(el => {
      el.textContent = moneyFormat(cart.total_price);
    });
  }

  /* ===== Recos fallback (extended mode) =====
     Si la section liquid a rendu aucune reco (metafield custom.ei_compagnons vide),
     on fetch via l'algo natif Shopify /recommendations/products.json.
     Si toujours rien : on cache la section entière. */
  async function loadRecosFallback() {
    const recosContainer = $('[data-ei-cart-recos]');
    if (!recosContainer) return;
    /* Si déjà des recos rendues par Liquid, ne rien faire */
    const existingRecos = $$('.ei-cp-reco', recosContainer);
    if (existingRecos.length > 0) return;
    /* Vérifier qu'il y a au moins un produit dans le panier */
    if (!cart || !cart.items || cart.items.length === 0) {
      recosContainer.style.display = 'none';
      return;
    }
    const firstProductId = cart.items[0].product_id;
    if (!firstProductId) {
      recosContainer.style.display = 'none';
      return;
    }
    try {
      const url = '/recommendations/products.json?product_id=' + firstProductId + '&limit=2&intent=related';
      const res = await fetch(url, { credentials: 'same-origin' });
      if (!res.ok) {
        recosContainer.style.display = 'none';
        return;
      }
      const data = await res.json();
      const products = (data.products || []).filter(p => p && p.handle);
      if (products.length === 0) {
        recosContainer.style.display = 'none';
        return;
      }
      const html = products.slice(0, 2).map(p => {
        const variants = p.variants || [];
        const firstVariant = variants.find(v => v.available) || variants[0];
        const variantId = firstVariant ? firstVariant.id : '';
        const priceCents = firstVariant ? parsePriceToCents(firstVariant.price) : 0;
        const img = p.featured_image || (p.images && p.images[0] && p.images[0].src);
        return `
          <div class="ei-cp-reco" data-ei-cart-reco data-ei-product-handle="${p.handle}">
            <a href="/products/${p.handle}" class="ei-cp-reco-img" tabindex="-1" aria-hidden="true">
              ${img ? `<img src="${img.replace(/(\.\w+)(\?|$)/, '_108x$1$2')}" alt="" width="54" height="54" loading="lazy">` : '<span aria-hidden="true">🛒</span>'}
            </a>
            <div class="ei-cp-reco-info">
              <p class="ei-cp-reco-name"><a href="/products/${p.handle}">${escapeHtml(p.title)}</a></p>
              <div class="ei-cp-reco-price">${moneyFormat(priceCents)}</div>
            </div>
            <button type="button" class="ei-cp-reco-add" data-ei-cart-action="add-reco" data-ei-product-handle="${p.handle}" data-ei-variant-id="${variantId}" aria-label="Ajouter ${escapeHtml(p.title)} au panier"${variantId ? '' : ' disabled aria-disabled="true"'}>+</button>
          </div>
        `;
      }).join('');
      /* Conserver le titre de section + injecter les cards après */
      const placeholder = recosContainer.querySelector('[data-ei-cart-recos-placeholder]');
      if (placeholder) {
        placeholder.outerHTML = html;
      } else {
        recosContainer.insertAdjacentHTML('beforeend', html);
      }
      recosContainer.style.display = '';
    } catch (err) {
      console.warn('[ei-cart] recos fallback failed', err);
      recosContainer.style.display = 'none';
    }
  }

  /* ===== Trust bar ===== */
  function randomizeTrustBar() {
    const text = $('[data-ei-trust-text]');
    if (!text) return;
    const idx = Math.floor(Math.random() * CONFIG.SLOGANS.length);
    text.textContent = CONFIG.SLOGANS[idx];
  }

  /* ===== Promesse d'expédition =====
     2026-08-19 — Une seule promesse sur tout le site, décidée par Margot.

     Avant, ce message était calculé selon l'heure de Paris et les jours fériés :
     « Avant 14h, jour ouvré : expédié aujourd'hui », « Après 14h : expédié dès
     demain », etc. Mécanique élégante, mais promesse fausse trop souvent —
     mesuré le 05-08 sur 12 mois de commandes EN LIGNE (sourceName=web, sans quoi
     les 4 914 commandes POS faussent tout) :
        expédié le jour même 36 %  ·  le lendemain 46 %  ·  J+2 13 %  ·  J+3+ 2 %
     Avant 14h, seules 56 % des commandes partent le jour même : le seuil de 14h
     a un effet réel, mais ne garantit rien. « Sous 48h » est tenu à 97 %.

     Le client voyait donc trois promesses différentes : « Expédié sous 48h » sur
     les collections, « expédié aujourd'hui » dans le panier, « Livraison 48h »
     sur le hero. On s'aligne sur la seule qui soit tenable — et on ne parle que
     d'expédition, jamais de livraison : le transport est sous-traité à Packlink,
     ni maîtrisé ni mesurable dans Shopify.

     Le calcul par jour ouvré et la fonction nextOpenDay() ont été retirés avec
     ce changement : plus rien ne les utilisait. CONFIG.JOURS_FERIES_2026 et
     JOURS_SEMAINE_FR restent en place, ils resserviront si l'on veut un jour
     réintroduire une date d'expédition estimée — mais alors sur des chiffres
     mesurés, pas sur une règle des 14h. */
  function updateShippingMessage() {
    const el = $('[data-ei-shipping-message]');
    if (!el) return;
    el.textContent = 'Expédié sous 48h';
  }

  /* ===== Multi-tab sync ===== */
  function setupBroadcastChannel() {
    if (typeof BroadcastChannel !== 'function') return;
    try {
      broadcastChannel = new BroadcastChannel(CONFIG.BROADCAST_CHANNEL_NAME);
      broadcastChannel.addEventListener('message', async (e) => {
        if (!e.data || isSyncingFromOtherTab) return;
        if (e.data.type === 'cart-updated') {
          isSyncingFromOtherTab = true;
          await fetchCart();
          updateCartBadge(cart.item_count);
          if (isOpen) await refreshPopoverFromSection();
          isSyncingFromOtherTab = false;
        }
      });
    } catch (e) { /* silencieux */ }
  }

  function notifyOtherTabs(type) {
    if (broadcastChannel && !isSyncingFromOtherTab) {
      try { broadcastChannel.postMessage({ type: type, ts: Date.now() }); } catch (e) {}
    }
  }

  /* ===== Event delegation ===== */
  function setupEventDelegation() {
    /* Intercept cart icon clicks (capture phase, avant Vegai jQuery handlers).
       Protection : ne jamais intercepter si le clic est sur/dans un input,
       un élément de recherche, un bouton "select", ou tout autre composant interactif
       qui n'est PAS un cart icon. */
    document.addEventListener('click', (e) => {
      /* Garde-fous : ignorer les zones connues qui ne sont pas des cart icons */
      if (e.target.closest('input, textarea, select, label, .js-search-destop, [role="search"], form[role="search"], form[action*="/search"], .search-form, .header-search, .ei-search')) {
        return;
      }

      /* Détection cart icon : préférer un anchor href=/cart, sinon classe spécifique sur l'élément cliqué directement */
      const directIcon = e.target.closest('a[href$="/cart"], a[href$="/cart/"], [data-ei-open-cart]');
      if (directIcon && !directIcon.closest('[data-ei-cart-popover]')) {
        e.preventDefault();
        e.stopPropagation();
        openExtendedFromIcon();
        return;
      }

      /* Fallback : .js-call-minicart MAIS uniquement si l'élément cliqué ou son parent immédiat porte la classe (pas un ancêtre lointain qui contiendrait aussi le search) */
      const minicartTrigger = e.target.closest('.js-call-minicart, .enj-cart-link');
      if (minicartTrigger && !minicartTrigger.closest('[data-ei-cart-popover]')) {
        /* Vérifier que ce n'est pas une fausse alerte : le trigger doit être un lien ou un bouton, pas un wrapper large */
        const tag = minicartTrigger.tagName;
        if (tag === 'A' || tag === 'BUTTON' || minicartTrigger.hasAttribute('role') && minicartTrigger.getAttribute('role') === 'button') {
          e.preventDefault();
          e.stopPropagation();
          openExtendedFromIcon();
        }
      }
    }, true);

    document.addEventListener('click', (e) => {
      /* Click outside container = close */
      const overlay = $('[data-ei-cart-popover]');
      if (isOpen && overlay && !overlay.hasAttribute('hidden')) {
        const container = $('[data-ei-cart-container]', overlay);
        if (e.target === overlay) {
          closePopover();
          return;
        }
      }

      /* Action buttons */
      const actionBtn = e.target.closest('[data-ei-cart-action]');
      if (actionBtn) {
        const action = actionBtn.dataset.eiCartAction;
        switch (action) {
          case 'open-extended':
            e.preventDefault();
            openExtendedFromIcon();
            return;
          case 'close':
            closePopover();
            return;
          case 'continue':
            closePopover();
            return;
          case 'view-cart':
            transitionCompactToExtended();
            return;
          case 'remove-line':
            handleRemoveClick(actionBtn);
            return;
          case 'add-reco':
            handleRecoAdd(actionBtn);
            return;
          case 'close-and-navigate':
            /* Liens dans état vide : laisse le navigateur naviguer après fermeture animée */
            closePopover();
            return;
          case 'checkout':
            handleCheckoutClick(e);
            return;
        }
      }

      /* Stepper */
      const stepperBtn = e.target.closest('[data-ei-stepper-action]');
      if (stepperBtn) {
        handleStepperClick(stepperBtn);
        return;
      }

      /* Promo toggle */
      if (e.target.closest('[data-ei-cart-promo-toggle]')) {
        handlePromoToggle();
        return;
      }
      /* Promo remove */
      if (e.target.closest('[data-ei-cart-promo-remove]')) {
        handlePromoRemove();
        return;
      }
      /* Confirm cancel/confirm remove */
      const confirmBtn = e.target.closest('[data-ei-confirm]');
      if (confirmBtn) {
        if (confirmBtn.dataset.eiConfirm === 'confirm') handleRemoveConfirm(confirmBtn);
        else handleRemoveCancel(confirmBtn);
        return;
      }
    });

    /* Promo form submit */
    document.addEventListener('submit', (e) => {
      const form = e.target.closest('[data-ei-cart-promo-form]');
      if (!form) return;
      e.preventDefault();
      handlePromoSubmit(form);
    });

    /* Echap pour fermer */
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && isOpen) {
        closePopover();
      }
      /* Focus trap dans le popover */
      if (e.key === 'Tab' && isOpen) {
        trapFocus(e);
      }
    });
  }

  function trapFocus(e) {
    const overlay = $('[data-ei-cart-popover]');
    if (!overlay || overlay.hasAttribute('hidden')) return;
    const container = $('[data-ei-cart-container]', overlay);
    const focusables = $$('button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])', container);
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  async function openExtendedFromIcon() {
    await fetchCart();
    await refreshPopoverFromSection();
    openPopover('extended');
    pushGA4('ei_drawer_open', { ei_source: 'cart_icon', ei_action: 'open' });
  }

  async function transitionCompactToExtended() {
    /* Animation : fade-out content, refresh, fade-in extended (in-place expand) */
    const overlay = $('[data-ei-cart-popover]');
    const container = $('[data-ei-cart-container]', overlay);
    if (!overlay || !container) return;
    container.style.opacity = '0';
    container.style.transition = 'opacity 0.2s ease-out';
    setTimeout(async () => {
      await refreshPopoverFromSection();
      container.classList.remove('ei-cp-mode-compact');
      container.classList.add('ei-cp-mode-extended');
      overlay.dataset.eiMode = 'extended';
      currentMode = 'extended';
      container.style.opacity = '1';
      setTimeout(() => { container.style.transition = ''; }, 250);
    }, 200);
  }

  /* ===== Init ===== */
  async function init() {
    const popover = $('[data-ei-cart-popover]');
    if (!popover) {
      console.warn('[ei-cart] popover not found in DOM');
      return;
    }

    setupEventDelegation();
    interceptAddToCart();
    setupBroadcastChannel();

    await fetchCart();
    if (cart) {
      lastSubBeforePromoCents = cart.original_total_price;
      updateCartBadge(cart.item_count);
    }
    updateShippingMessage();
    /* Initial load : tenter le fallback recos au cas où la page rendrait avec un panier non vide
       et que le metafield serait absent */
    loadRecosFallback();

    /* Expose API publique */
    window.eiCartPopover = {
      open: openPopover,
      close: closePopover,
      refresh: refreshPopoverFromSection,
      getCart: () => cart,
      isOpen: () => isOpen,
      getMode: () => currentMode
    };

    dispatchEi('ei:cart-popover-ready', { cart: cart });
  }

  /* ============================================================
     EI Mama — Modal rappel "petit mot oublié" au clic Commander
     ============================================================
     Conditions de déclenchement :
       - ≥1 line item avec properties['Coffret Mamma'] ou ['Coffret Mamma composé']
       - ET ce line item n'a PAS properties['Carton choisi']
       - ET sessionStorage 'ei_mama_pmr_dismissed' ≠ '1'
     Non bloquant : "Non merci" → sessionStorage + redirect /checkout
     Le bouton "Ajouter mon mot" → click sur [data-ei-mama-add-mot-from-cart]
     du panier (qui ouvre la popup petitmot via ei-mama.js).
     Fallback si ei-mama.js absent : redirect /pages/la-collection-mamma.
  */
  function mamaCoffretsSansCarton(cartData) {
    if (!cartData || !Array.isArray(cartData.items)) return [];
    return cartData.items.filter(item => {
      const props = item.properties || {};
      const isMamaCoffret = props['Coffret Mamma'] || props['Coffret Mamma composé'];
      const hasCarton = props['Carton choisi'];
      return isMamaCoffret && !hasCarton;
    });
  }

  function openMamaPmr() {
    const overlay = document.getElementById('ei-mama-petitmot-reminder');
    if (!overlay) return;
    overlay.hidden = false;
    overlay.classList.remove('is-closing');
    document.body.style.overflow = 'hidden';
    const modal = overlay.querySelector('[data-ei-mama-pmr-modal]');
    if (modal) modal.focus();
    pushGA4('ei_mama_pmr_shown', { ei_source: 'cart_popover' });
  }

  function closeMamaPmr(after) {
    const overlay = document.getElementById('ei-mama-petitmot-reminder');
    if (!overlay) return;
    overlay.classList.add('is-closing');
    setTimeout(() => {
      overlay.hidden = true;
      overlay.classList.remove('is-closing');
      document.body.style.overflow = '';
      if (typeof after === 'function') after();
    }, 240);
  }

  document.addEventListener('ei:checkout-clicked', function (evt) {
    /* SHUNT 2026-08-05 : hors opération calendaire Mamma, ce rappel est coupé.
       Il intercepte le même clic que L'Étal — deux modales se superposeraient.
       Réactivation : case "Activer le rappel petit mot" dans le Customizer. */
    const popover = document.querySelector('[data-ei-cart-popover]');
    if (!popover || popover.dataset.eiMamaPmrActive !== 'true') return;

    if (sessionStorage.getItem('ei_mama_pmr_dismissed') === '1') return;
    if (!cart) return;
    const missing = mamaCoffretsSansCarton(cart);
    if (missing.length === 0) return;
    evt.preventDefault();
    openMamaPmr();
  });

  document.addEventListener('click', function (e) {
    /* Close : backdrop, croix, ou clic hors modal */
    if (e.target.closest('[data-ei-mama-pmr-close]')) {
      closeMamaPmr();
      return;
    }
    const overlayEl = document.getElementById('ei-mama-petitmot-reminder');
    if (overlayEl && !overlayEl.hidden && e.target === overlayEl) {
      closeMamaPmr();
      return;
    }
    /* "Non merci, continuer sans" → flag session + checkout */
    if (e.target.closest('[data-ei-mama-pmr-skip]')) {
      sessionStorage.setItem('ei_mama_pmr_dismissed', '1');
      pushGA4('ei_mama_pmr_skip', { ei_source: 'cart_popover' });
      closeMamaPmr(function () { window.location.href = '/checkout'; });
      return;
    }
    /* "Ajouter mon petit mot" → ouvre directement la popup petit mot (ei-mama-popup-petitmot).
       On bypasse cartBtn.click() (peu fiable sur iOS) en manipulant directement le DOM. */
    if (e.target.closest('[data-ei-mama-pmr-add]')) {
      pushGA4('ei_mama_pmr_add', { ei_source: 'cart_popover' });

      /* 1) Ferme la modale pmr (instantané, pas de setTimeout iOS) */
      const overlayPmr = document.getElementById('ei-mama-petitmot-reminder');
      if (overlayPmr) {
        overlayPmr.hidden = true;
        overlayPmr.classList.remove('is-closing');
      }
      document.body.style.overflow = '';

      /* 2) Met à jour le contexte petit mot global (utilisé par ei-mama.js au validate) */
      const cartBtn = document.querySelector('[data-ei-mama-add-mot-from-cart]');
      if (cartBtn && window.eiMamaState) {
        window.eiMamaState.petitMotContext = {
          source: cartBtn.dataset.eiMamaCartSource || 'cart-coffret',
          lineKey: cartBtn.dataset.eiLineKey || '',
          coffretTitle: cartBtn.dataset.eiCoffretTitle || ''
        };
      }

      /* 3) Ouvre directement l'overlay popup petitmot (rendu sur page Mamma uniquement) */
      const petitMotOverlay = document.querySelector('[data-ei-mama-popup="petitmot"]');
      if (petitMotOverlay) {
        /* Ferme d'abord les autres overlays mama */
        document.querySelectorAll('.ei-mama-popup-overlay').forEach(function (ov) {
          ov.setAttribute('hidden', '');
          ov.setAttribute('aria-hidden', 'true');
        });
        petitMotOverlay.removeAttribute('hidden');
        petitMotOverlay.setAttribute('aria-hidden', 'false');
        document.body.classList.add('ei-mama-popup-open');
      } else {
        /* Pas sur la page Mamma → fallback redirection */
        window.location.href = '/pages/la-collection-mamma';
      }
      return;
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      const overlay = document.getElementById('ei-mama-petitmot-reminder');
      if (overlay && !overlay.hidden) closeMamaPmr();
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
